import { TypographyCaption } from '@heidi/design-system/typography';
import { formatCjkAwareDisplayName } from '@heidi/utils/i18n';

interface NoteSignatureProps {
  firstName: string;
  lastName: string;
  specialtyName?: string | undefined;
  clinicName?: string | undefined;
  displayLanguage?: string | null;
}

/**
 * Personalised signature block used by the clinical-note tab in the
 * onboarding-v2 preview. Uses the tokenised `font-noto-serif` (resolves
 * to `var(--font-noto-serif)` in tailwind-config) plus italic + size to
 * evoke a hand-written sign-off — there's no Typography variant for an
 * italic-signature shape, so this is one of the rare deliberate raw
 * font-style/size applications carried over from Jacob's inline
 * NoteSignature in MicTestPreview (#9177). The document/referral tab
 * uses a simpler "Kind regards, / <Name>" pattern instead — see
 * `ReferralContentRenderer`.
 */
export function NoteSignature({
  firstName,
  lastName,
  specialtyName,
  clinicName,
  displayLanguage,
}: NoteSignatureProps) {
  const fullName = formatCjkAwareDisplayName({ firstName, lastName }, displayLanguage);
  if (!fullName) return null;

  return (
    <div className="border-t border-border pt-3">
      <div className="flex flex-col">
        <span className="font-noto-serif text-xl italic text-text-primary">
          {fullName}
        </span>
        {specialtyName && (
          <TypographyCaption className="text-text-secondary">
            {specialtyName}
          </TypographyCaption>
        )}
        {clinicName && (
          <TypographyCaption className="text-text-tertiary">
            {clinicName}
          </TypographyCaption>
        )}
      </div>
    </div>
  );
}
